/**
 * config.js - Configuration loading from multiple sources
 *
 * Loads configuration from:
 * 1. <meta name="htmlstar:*"> tags
 * 2. <script type="htmlstar/config"> JSON blocks
 *
 * Note: <html>/<body>/ancestor data-* attributes are handled in cascade.js.
 */

let cachedConfig = null;

/**
 * Load configuration from a JSON script block
 * @returns {Object|null}
 */
function loadJSONConfig() {
  const script = document.querySelector('script[type="htmlstar/config"]');
  if (!script) return null;

  try {
    return JSON.parse(script.textContent);
  } catch (e) {
    console.error('htmlstar: Invalid JSON config', e);
    return null;
  }
}

/**
 * Load configuration from meta tags
 * @returns {Object}
 */
function loadMetaConfig() {
  const config = {};
  const metas = document.querySelectorAll('meta[name^="htmlstar:"]');

  for (const meta of metas) {
    const name = meta.getAttribute('name').replace('htmlstar:', '');
    const content = meta.getAttribute('content');
    config[name] = content;
  }

  return config;
}

/**
 * Convert kebab-case to camelCase
 * e.g., 'persist-scope' -> 'persistScope'
 *
 * @param {string} str
 * @returns {string}
 */
function toCamelCase(str) {
  return str.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

/**
 * Convert camelCase to kebab-case
 * e.g., 'persistScope' -> 'persist-scope'
 *
 * @param {string} str
 * @returns {string}
 */
function toKebabCase(str) {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * Try a set of normalized keys against a config object
 *
 * @param {Object} rules
 * @param {string[]} keys
 * @returns {string|undefined}
 */
function getRuleValue(rules, keys) {
  for (const key of keys) {
    if (rules[key] !== undefined) {
      return String(rules[key]);
    }
  }
  return undefined;
}

/**
 * Get merged configuration from all sources
 * Results are cached for performance
 *
 * @param {boolean} refresh - Force refresh of cached config
 * @returns {Object}
 */
export function getConfig(refresh = false) {
  if (cachedConfig && !refresh) {
    return cachedConfig;
  }

  const jsonConfig = loadJSONConfig() || {};
  const metaConfig = loadMetaConfig();

  cachedConfig = {
    defaults: {
      ...metaConfig,
      ...(jsonConfig.defaults || {})
    },
    selectors: jsonConfig.selectors || {}
  };

  return cachedConfig;
}

/**
 * Get config value for a specific selector match
 * Used by cascade.js when resolving attributes
 *
 * @param {HTMLElement} el - Element to check
 * @param {string} attr - Attribute name
 * @returns {string|undefined}
 */
export function getConfigForElement(el, attr) {
  const config = getConfig();
  const raw = String(attr);
  const keys = Array.from(new Set([
    raw,
    toCamelCase(raw),
    toKebabCase(raw)
  ]));

  // Check selector-based rules (most specific first)
  for (const [selector, rules] of Object.entries(config.selectors)) {
    if (el.matches(selector)) {
      const value = getRuleValue(rules, keys);
      if (value !== undefined) return value;
    }
  }

  // Check defaults
  return getRuleValue(config.defaults, keys);
}

/**
 * Clear cached configuration
 * Useful for testing or dynamic config changes
 */
export function clearConfigCache() {
  cachedConfig = null;
}
